import React, { useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import { Map } from 'immutable'
import _get from 'lodash/get'
import { getBoundActions } from '../../actions/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { getPrimary } from '../../services/languages/index'
import {
  isSubtypeEvent,
  isGuide,
  isEpisode,
  isVideo,
  isSeries,
  isSeriesNoEpisodes,
  isGuideNotStartedYet,
  isGaiaPlusFeature,
  getFeatureMedia,
  getFeaturedContent,
  featureAllowedWithUserSubscription,
} from '../../services/content/index'
import { hasLiveAccessEntitlement, isEntitled } from '../../services/subscription/index'
import { shouldShowWinbackCheckout, WINBACK_FLAG, WINBACK_VARIATION } from '../../services/winback/index'
import { URL_CHECKOUT, URL_GAIA_PLUS } from '../../services/url/constants'
import { get as getConfig } from '../../config/index'
import WatchV2 from '../Watch.v2/index'
import OptimizelyFeature from '../OptimizelyFeature/index'
import Button, { TYPES } from '../Button/index'
import { ICON_TYPES } from '../Icon.v2/index'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_BUTTON } from '../ButtonSignUp/index'
import DetailPageV3ActionTrailer from './DetailPageV3ActionButtons/DetailPageV3ActionTrailer'
import DetailPageV3ActionComments from './DetailPageV3ActionButtons/DetailPageV3ActionComments'
import {
  shouldShowWinbackButton,
  handleSignUpClicked,
  buildJumbotronUpstreamContext,
  getWatchButtonText,
} from './utils'

const config = getConfig()
const guidePolicyGaiaPlusUpgrade = !!_get(config, ['features', 'guidePolicyGaiaPlusUpgrade'])

/**
 * Watch Now Button
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @param {Boolean} props.isSeriesDetailPage - Is the current page a series page
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isEvent - Is series event
 * @param {Boolean} props.isGuideDetailPage - is a Guide page
 * @param {Boolean} props.hasLiveAccess - If the user has a Gaia+ subscription
 * @param {Boolean} props.featureIsGaiaPlus - If the feature media is Gaia+ only
 * @param {Boolean} props.isLoggedIn - If the user is logged in
 * @param {Map} props.auth - The auth data
 * @param {Map} props.ga4EventData - The GA4 event data
 * @param {Map} props.upstreamContext - The upstream context
 * @returns {JSX.Element} Watch now button
 */
function WatchButtons (props) {
  const {
    detailData,
    isSeriesDetailPage,
    staticText,
    isEvent,
    isGuideDetailPage,
    hasLiveAccess,
    featureIsGaiaPlus,
    isLoggedIn,
    auth,
    ga4EventData,
    upstreamContext,
  } = props
  const contentType = detailData.get('contentType')
  const featuredContent = getFeaturedContent({ data: detailData })
  const nodeData = isSeriesDetailPage ? featuredContent : detailData

  const jumbotronUpstreamContext = buildJumbotronUpstreamContext({
    auth,
    nodeData,
    parentNodeData: detailData,
    ga4EventData,
    upstreamContext,
  })
  const isGuideOrEvent = isEvent || isGuideDetailPage
  const feature = getFeatureMedia({ data: detailData, contentType })
  const watchPath = isSeriesDetailPage ? featuredContent.get('path', '') : detailData.get('path', '')
  const watchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  const isFreeAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'isFreeAllowed'])
  const isFeatureAllowed = featureAllowedWithUserSubscription({ data: nodeData })
  const isFeatureAvailableToUser = isFreeAllowed || watchAccess || isFeatureAllowed
  const isCountryAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'], true)

  if (Map.isMap(feature) && feature.size === 0) {
    return null
  }

  if (!isCountryAllowed && !watchAccess) {
    return (
      <div className="detail-page-v3__jumbotron-unavailable-text">
        {staticText.get('notAvailableInArea')}
      </div>
    )
  }

  if (isGuideOrEvent || featureIsGaiaPlus) {
    return (
      <div className="detail-jumbotron__featured-action-buttons">
      {isFeatureAvailableToUser && <WatchV2
        upstreamContext={jumbotronUpstreamContext}
        className="detail-page-v3__jumbotron-primary-button"
        text={staticText.get('watchNow')}
        url={watchPath}
        watchAccess={watchAccess}
        isFree={isFreeAllowed}
        iconType={ICON_TYPES.PLAY}
        buttonType={TYPES.ICON}
      />}
      {!hasLiveAccess && !watchAccess && <Button
        className="detail-page-v3__jumbotron-primary-button"
        url={URL_GAIA_PLUS}
        type={TYPES.LABELED}
        label={staticText.getIn(['watchFullSeries'])}>
        {isLoggedIn ? staticText.getIn(['upgrade']) : staticText.getIn(['signUp'])}
      </Button>}
    </div>
    )
  }

  if (!watchAccess && !isGuideOrEvent) {
    return (
      <ButtonSignUp
        text={staticText.getIn(['signUpToWatch'])}
        buttonClass="detail-page-v3__jumbotron-primary-button"
        type={BUTTON_SIGN_UP_TYPE_BUTTON}
        onClick={() => handleSignUpClicked(props)}
        scrollToTop
      />
    )
  }

  return (
    <WatchV2
      iconType={ICON_TYPES.PLAY}
      upstreamContext={jumbotronUpstreamContext}
      buttonType={TYPES.ICON}
      className="detail-page-v3__jumbotron-primary-button"
      text={staticText.get('watchNow')}
      url={watchPath}
      watchAccess={watchAccess}
      isFree={isFreeAllowed}
    />
  )
}

/**
 * Access Buttons
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @param {Boolean} props.isSeriesDetailPage - Is the current page a series page
 * @param {Boolean} props.isEpisodeDetailPage - Is the current page an episode page
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isEvent - Is series event
 * @param {Boolean} props.isGuideDetailPage - is a Guide page
 * @param {Boolean} props.hasLiveAccess - If the user has a Gaia+ subscription
 * @returns {JSX.Element} Watch button
 */
function AccessButtons (props) {
  const {
    detailData,
    isSeriesDetailPage,
    staticText,
    isEvent,
    isGuideDetailPage,
    hasLiveAccess,
    hasEntitlement,
    auth,
    ga4EventData,
    upstreamContext,
    viewportWidth,
  } = props
  const contentType = detailData.get('contentType')
  const featuredContent = getFeaturedContent({ data: detailData })
  const nodeData = isSeriesDetailPage ? featuredContent : detailData

  const feature = getFeatureMedia({ data: detailData, contentType })
  if (!Map.isMap(feature) || feature.size === 0) {
    return null
  }

  const isGuideOrEvent = isEvent || isGuideDetailPage
  const watchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  const isFeatureAllowed = featureAllowedWithUserSubscription({ data: nodeData })
  const isFeatureAvailableToUser = watchAccess || isFeatureAllowed
  const isGaiaPlusOnly = isGaiaPlusFeature(feature)
  const isCountryAllowed = feature.getIn(['contentPolicy', 'accessCheck', 'countryAllowed'], true)

  const showUpgradeButton = !hasLiveAccess && isGaiaPlusOnly && isGuideOrEvent && !watchAccess
  const showNotAvailableMessage = !watchAccess && !isCountryAllowed
  const showSignUpButton = !isFeatureAvailableToUser
    && isCountryAllowed
    && !hasEntitlement
    && !showUpgradeButton

  const renderWatchButton = () => {
    const watchPath = nodeData.get('path', '')
    const isFreeAllowed = !!feature.getIn(['contentPolicy', 'accessCheck', 'isFreeAllowed'])

    const updatedUpstreamContext = buildJumbotronUpstreamContext({
      auth,
      nodeData,
      parentNodeData: detailData,
      ga4EventData,
      upstreamContext,
    })

    const watchText = getWatchButtonText({
      featuredContent,
      viewportWidth,
      staticText,
      isSeriesDetailPage,
    })

    return <WatchV2
      upstreamContext={updatedUpstreamContext}
      className="detail-page-v3__jumbotron-primary-button"
      url={watchPath}
      watchAccess={watchAccess}
      isFree={isFreeAllowed}
      iconType={ICON_TYPES.PLAY}
      buttonType={TYPES.ICON}
      text={watchText}
    />
  }

  const renderSignUpButton = () => (
    <ButtonSignUp
      text={staticText.getIn(['signUpToWatch'])}
      buttonClass="detail-page-v3__jumbotron-primary-button"
      type={BUTTON_SIGN_UP_TYPE_BUTTON}
      scrollToTop
      onClick={() => handleSignUpClicked(props)}
    />
  )

  const renderUpgradeButton = () => (
    <Button
      className="detail-page-v3__jumbotron-primary-button"
      url={URL_GAIA_PLUS}
      type={TYPES.LABELED}
      label={staticText.getIn(['watchFullSeries'])}
    >
      {hasEntitlement ? staticText.getIn(['upgrade']) : staticText.getIn(['signUp'])}
    </Button>
  )

  const renderNotAvailableMessage = () => (
    <div className="detail-page-v3__jumbotron-unavailable-text">
      {staticText.get('notAvailableInArea')}
    </div>
  )

  return (
    <>
      {watchAccess && renderWatchButton()}
      {showSignUpButton && renderSignUpButton()}
      {showUpgradeButton && renderUpgradeButton()}
      {showNotAvailableMessage && renderNotAvailableMessage()}
    </>
  )
}

/**
 * Winback Flow Button for users who have lost access
 * NOTE: move to DetailAccessButtons once the feature guidePolicyGaiaPlusUpgrade is enabled
 * @param {Object} props - The props
 * @param {Map} props.detailData - The content data
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Winback buttons (subscribe + watch trailer)
 */
function WinbackButton (props) {
  const {
    staticText,
  } = props

  return (
    <Button
      className="detail-page-v3__jumbotron-primary-button"
      url={URL_CHECKOUT}
      type={TYPES.PRIMARY}
      onClick={() => handleSignUpClicked(props)}
    >
      {staticText.getIn(['subscribe'])}
    </Button>
  )
}

/**
 * @param {Object} props - The props
 * @param {Map} props.detailData - The detail data
 * @param {Map} props.ga4EventData - The GA4 event data
 * @returns {JSX.Element} DetailPageV3JumbotronActions
 */
const DetailPageV3JumbotronActions = (props) => {
  const {
    detailData = Map(),
    ga4EventData,
    updateOptimizelyFeatureUser,
  } = props

  // Winback Flow:Update the user_is_winback attribute for the winback flow
  useEffect(() => {
    const renderWinbackButton = shouldShowWinbackButton(props)
    updateOptimizelyFeatureUser({
      page_has_winback_ctas: renderWinbackButton,
    })
  }, [detailData])

  return (
    <div className="detail-page-v3__jumbotron-actions">
      <>
        <OptimizelyFeature
          flag={WINBACK_FLAG}
          variation="control"
          isDefault={true}
          anon={false}
          noWrapper
        >
          {
            guidePolicyGaiaPlusUpgrade
              ? <AccessButtons {...props} />
              : <WatchButtons {...props} />
          }
        </OptimizelyFeature>
        <OptimizelyFeature
          flag={WINBACK_FLAG}
          variation={WINBACK_VARIATION}
          noWrapper
        >
          <WinbackButton {...props} />
        </OptimizelyFeature>
      </>
      <div className="detail-page-v3__jumbotron-actions-secondary">
        <DetailPageV3ActionTrailer detailData={detailData} ga4EventData={ga4EventData} />
        <DetailPageV3ActionComments url={`${detailData.get('path')}#commentsBlock`} />
      </div>
    </div>
  )
}

export default connectRedux((state, props) => {
  const {
    auth,
    user,
    userAccount,
    paytrack,
  } = state
  const { detailData = Map() } = props
  const contentType = detailData.get('contentType')
  const contentSubtype = detailData.get('contentSubtype')
  const seriesContentSubtype = detailData.get('seriesContentSubtype')
  const displayType = detailData.get('displayType')
  const data = detailData
  const subscriptions = auth.get('subscriptions')
  const hasLiveAccess = hasLiveAccessEntitlement(subscriptions)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const isEpisodeDetailPage = isEpisode({ contentType, data })
  const isEvent = isEpisodeDetailPage
    ? isSubtypeEvent({ contentSubtype: seriesContentSubtype })
    : isSubtypeEvent({ contentSubtype })
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const hasEntitlement = isEntitled(subscriptions)

  // Winback Flow
  const isWinbackFlowActive = shouldShowWinbackCheckout(auth, user, userAccount, paytrack)
  return {
    auth: state.auth,
    upstreamContext: state.upstreamContext,
    staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
    isVideoDetailPage: isVideo({ contentType, data }),
    isEpisodeDetailPage: isEpisode({ contentType, data }),
    isSeriesDetailPage: isSeries({ contentType }),
    isEvent,
    isGuideDetailPage: isGuide({ contentSubtype, displayType }),
    isSeriesDetailPageNoEpisodes: isSeriesNoEpisodes({ contentType, data }),
    isGuideAndNotStarted: isGuideNotStartedYet({ contentSubtype, displayType, data }),
    featureIsGaiaPlus: isGaiaPlusFeature(detailData.get('featureMedia')),
    hasLiveAccess,
    userLanguage,
    isLoggedIn,
    hasEntitlement,
    isWinbackFlowActive,
    viewportWidth: state.app.getIn(['viewport', 'width']),
  }
}, (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
    setUpstreamContext: actions.upstreamContext.setUpstreamContext,
    sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
    updateOptimizelyFeatureUser: actions.optimizelyFeature.updateOptimizelyFeatureUser,
  }
})(DetailPageV3JumbotronActions)
